/* eslint-disable max-lines */
import { Injectable, ViewContainerRef, inject } from '@angular/core';

import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { tap } from 'rxjs';
import { map } from 'rxjs/operators';

import { AdminsSelectDialogComponent, AdminsSelectDialogResult } from '@core/account-smart-ui';
import type { User } from '@core/account-types';
import { filterDefined } from '@core/common-legacy';
import { I18nService } from '@core/i18n-legacy';
import type { OfferSimpleListItem } from '@core/offers-types';
import type { Requisition } from '@core/requisitions-types';
import { RequisitionStatus } from '@core/requisitions-types';
import { DialogService, DialogWidth } from '@core/user-interface-legacy';
import { RequisitionsActions, UpdateContext } from '@recruitee/requisitions-data';

import type { ApprovalRequestDialogData } from '../dialogs/approval-request-dialog/approval-request-dialog.component';
import { ApprovalRequestDialogComponent } from '../dialogs/approval-request-dialog/approval-request-dialog.component';
import { AssignedJobsDialogComponent } from '../dialogs/assigned-jobs-dialog/assigned-jobs-dialog.component';
import { ConfirmCreateJobDialogComponent } from '../dialogs/confirm-create-job-dialog/confirm-create-job-dialog.component';
import type { CreateBlankRequisitionDialogData } from '../dialogs/create-blank-requisition-dialog/create-blank-requisition-dialog.component';
import { CreateBlankRequisitionDialogComponent } from '../dialogs/create-blank-requisition-dialog/create-blank-requisition-dialog.component';
import { CreateRequisitionDialogComponent } from '../dialogs/create-requisition-dialog/create-requisition-dialog.component';
import type { DuplicateRequisitionDialogData } from '../dialogs/duplicate-requisition-dialog/duplicate-requisition-dialog.component';
import { DuplicateRequisitionDialogComponent } from '../dialogs/duplicate-requisition-dialog/duplicate-requisition-dialog.component';
import type {
  DialogResult,
  RequisitionApprovedDialogData,
} from '../dialogs/requisition-approved-success-dialog/requisition-approved-success-dialog.component';
import { RequisitionApprovedSuccessDialogComponent } from '../dialogs/requisition-approved-success-dialog/requisition-approved-success-dialog.component';
import { SubmitRejectionDialogComponent } from '../dialogs/submit-rejection-dialog/submit-rejection-dialog.component';

@Injectable()
export class RequisitionsDialogsService {
  private readonly dialogService = inject(DialogService);
  private readonly i18n = inject(I18nService);
  private readonly store = inject(Store);

  private readonly texts = {
    changeDepartment: {
      title: this.i18n.translate('app.requisitions.confirm_change_department.title'),
      message: this.i18n.translate('app.requisitions.confirm_change_department.message'),
      agreeLabel: this.i18n.translate('app.requisitions.confirm_change_department.agree_label'),
    },
    changeDepartmentRequestApproval: {
      title: this.i18n.translate(
        'app.requisitions.confirm_change_department_request_approval.title',
      ),
      message: this.i18n.translate(
        'app.requisitions.confirm_change_department_request_approval.message',
      ),
      agreeLabel: this.i18n.translate(
        'app.requisitions.confirm_change_department_request_approval.agree_label',
      ),
    },
    duplicate: {
      title: this.i18n.translate('app.requisitions.confirm_duplicate.title'),
      message: this.i18n.translate('app.requisitions.confirm_duplicate.message'),
      agreeLabel: this.i18n.translate('app.common.duplicate'),
    },
    cancelRequest: {
      title: this.i18n.translate('app.requisitions.confirm_cancel_request.title'),
      message: this.i18n.translate('app.requisitions.confirm_cancel_request.message'),
      agreeLabel: this.i18n.translate('app.requisitions.confirm_cancel_request.agree_label'),
    },
    approveRequest: {
      title: this.i18n.translate('app.requisitions.confirm_approve_request.title'),
      message: this.i18n.translate('app.requisitions.confirm_approve_request.message'),
      agreeLabel: this.i18n.translate('app.requisitions.confirm_approve_request.agree_label'),
    },
    deleteInvitation: {
      title: this.i18n.translate(`app.common.delete_confirmaton`),
      message: this.i18n.translate(`app.invitation_form.delete_confirmation`),
    },
    removeTeamMember: {
      title: this.i18n.translate('app.invitation_form.are_you_sure'),
      message: this.i18n.translate('app.requisitions.team_members.confirm_remove_member.message'),
      agreeLabel: this.i18n.translate(`app.invitation_form.remove`),
    },
    deleteAttachment: {
      title: this.i18n.translate('app.requisitions.files.confirm_delete_dialog.title'),
      message: this.i18n.translate('app.requisitions.files.confirm_delete_dialog.message'),
      agreeLabel: this.i18n.translate('app.candidate.delete_attachment_agree_label'),
    },
    deleteOpenings: (count: number) => ({
      title: this.i18n.translate('app.requisitions.confirm_delete_opening.title', { count }),
      message: this.i18n.translate('app.requisitions.confirm_delete_opening.message', { count }),
      agreeLabel: this.i18n.translate('app.requisitions.confirm_delete_opening.agree_label', {
        count,
      }),
    }),
    deleteRequisition: (name: string) => ({
      title: this.i18n.translate('app.requisitions.confirm_delete_requisition.title'),
      message: this.i18n.translate('app.requisitions.confirm_delete_requisition.message', { name }),
      agreeLabel: this.i18n.translate('app.requisitions.confirm_delete_requisition.agree_label'),
      checkboxLabel: this.i18n.translate(
        'app.requisitions.confirm_delete_requisition.checkbox_label',
      ),
    }),
    unassignCandidate: {
      title: this.i18n.translate('app.requisitions.openings.unassign_confirm_dialog.title'),
      message: this.i18n.translate('app.requisitions.openings.unassign_confirm_dialog.message'),
      agreeLabel: this.i18n.translate(
        'app.requisitions.openings.unassign_confirm_dialog.agree_label',
      ),
    },
    archive: {
      defaultAgreeLabel: this.i18n.translate(
        'app.requisitions.confirm_archive.default.agree_label',
      ),
      defaultTitle: this.i18n.translate('app.requisitions.confirm_archive.default.title'),
      defaultMessage: this.i18n.translate('app.requisitions.confirm_archive.default.message'),
      pendingMessage: this.i18n.translate(
        'app.requisitions.confirm_archive.pending_status_message',
      ),
      approvedMessage: this.i18n.translate(
        'app.requisitions.confirm_archive.approved_status_message',
      ),
      archiveRequisition: this.i18n.translate(
        'app.requisitions.confirm_archive.archive_requisition',
      ),
    },
    bulkArchive: (count: number) => ({
      agreeLabel: this.i18n.translate('app.requisitions.confirm_archive.bulk.agree_label', {
        count,
      }),
      title: this.i18n.translate('app.requisitions.confirm_archive.bulk.title', { count }),
      defaultMessage: this.i18n.translate('app.requisitions.confirm_archive.bulk.default_message', {
        count,
      }),
      pendingMessage: this.i18n.translate(
        'app.requisitions.confirm_archive.bulk.pending_status_message',
        { count },
      ),
      approvedMessage: this.i18n.translate(
        'app.requisitions.confirm_archive.bulk.approved_status_message',
        { count },
      ),
    }),
    retrieve: {
      agreeLabel: this.i18n.translate('app.requisitions.confirm_retrieve.agree_label.one'),
      title: this.i18n.translate('app.requisitions.confirm_retrieve.title.one'),
      defaultMessage: this.i18n.translate('app.requisitions.confirm_retrieve.default_message.one'),
      draftAndRejectedMessage: this.i18n.translate(
        'app.requisitions.confirm_retrieve.draft_and_rejected_status_message.one',
      ),
      approvedMessage: this.i18n.translate(
        'app.requisitions.confirm_retrieve.approved_status_message.one',
      ),
    },
    bulkRetrieve: (count: number) => ({
      agreeLabel: this.i18n.translate('app.requisitions.confirm_retrieve.agree_label', {
        count,
      }),
      title: this.i18n.translate('app.requisitions.confirm_retrieve.title', { count }),
      defaultMessage: this.i18n.translate('app.requisitions.confirm_retrieve.default_message', {
        count,
      }),
      draftMessage: this.i18n.translate(
        'app.requisitions.confirm_retrieve.bulk.draft_and_rejected_status_message',
        { count },
      ),
      approvedMessage: this.i18n.translate(
        'app.requisitions.confirm_retrieve.bulk.approved_status_message',
        { count },
      ),
    }),
  };

  public openCreateFirstStep$() {
    return this.dialogService.openCustom(CreateRequisitionDialogComponent, {
      width: DialogWidth.Small,
    });
  }

  public openCreateSecondStepBlank$(data: CreateBlankRequisitionDialogData) {
    return this.dialogService.openCustom(
      CreateBlankRequisitionDialogComponent,
      { width: DialogWidth.Small },
      data,
    );
  }

  public openCreateSecondStepDuplicate$(data: DuplicateRequisitionDialogData) {
    return this.dialogService.openCustom(
      DuplicateRequisitionDialogComponent,
      { width: DialogWidth.Small },
      data,
    );
  }

  public confirmDuplicate$() {
    return this.dialogService.information({
      width: DialogWidth.SmallXX,
      ...this.texts.duplicate,
    });
  }

  public confirmCreateJob$() {
    return this.dialogService.openCustom(ConfirmCreateJobDialogComponent, {
      width: DialogWidth.SmallXX,
    });
  }

  public confirmCancelRequest$() {
    return this.dialogService.warning({
      width: DialogWidth.SmallXX,
      ...this.texts.cancelRequest,
    });
  }

  public confirmApproveRequest$() {
    return this.dialogService.information({
      width: DialogWidth.SmallXX,
      ...this.texts.approveRequest,
    });
  }

  public confirmRejectRequest$(): Observable<string> {
    return this.dialogService.openCustom(SubmitRejectionDialogComponent, {
      width: DialogWidth.Medium,
    }).afterClosed;
  }

  public confirmUnassignCandidate$() {
    return this.dialogService.warning({
      width: DialogWidth.SmallXX,
      ...this.texts.unassignCandidate,
    });
  }

  public confirmArchive$(requisitionStatus: RequisitionStatus) {
    let message = this.texts.archive.defaultMessage;
    let agreeLabel = this.texts.archive.defaultAgreeLabel;

    switch (requisitionStatus) {
      case RequisitionStatus.Pending:
        message = `${this.texts.archive.defaultMessage}<br><br><b>${this.texts.archive.pendingMessage}</b>`;
        agreeLabel = this.texts.archive.archiveRequisition;
        break;
      case RequisitionStatus.Approved:
        message = `${this.texts.archive.defaultMessage}<br><br><b>${this.texts.archive.approvedMessage}</b>`;
        agreeLabel = this.texts.archive.archiveRequisition;
        break;
      default:
        break;
    }

    return this.dialogService.warning({
      width: DialogWidth.SmallXX,
      message,
      title: this.texts.archive.defaultTitle,
      agreeLabel,
    });
  }

  public confirmBulkArchive$(requisitions: Requisition[]) {
    const { defaultMessage, title, agreeLabel } = this.texts.bulkArchive(requisitions.length);

    const { approvedCount, pendingCount } = requisitions.reduce(
      (acc, req) => {
        const approvedCount =
          req.status === RequisitionStatus.Approved ? acc.approvedCount + 1 : acc.approvedCount;
        const pendingCount =
          req.status === RequisitionStatus.Pending ? acc.pendingCount + 1 : acc.pendingCount;
        return { ...acc, approvedCount, pendingCount };
      },
      { approvedCount: 0, pendingCount: 0 },
    );

    let message = defaultMessage;

    if (approvedCount && pendingCount) {
      message = `${defaultMessage}<br><br><ul><li>${
        this.texts.bulkArchive(approvedCount).approvedMessage
      }</li><li>${this.texts.bulkArchive(pendingCount).pendingMessage}</li></ul>`;
    } else if (approvedCount) {
      message = `${defaultMessage}<br><br><ul><li>${
        this.texts.bulkArchive(approvedCount).approvedMessage
      }</li></ul>`;
    } else if (pendingCount) {
      message = `${defaultMessage}<br><br><ul><li>${
        this.texts.bulkArchive(pendingCount).pendingMessage
      }</li></ul>`;
    }

    return this.dialogService.warning({
      width: DialogWidth.SmallXX,
      message,
      title,
      agreeLabel,
    });
  }

  public confirmRetrieve$(requisitionStatus: RequisitionStatus) {
    const { title, defaultMessage, agreeLabel, draftAndRejectedMessage, approvedMessage } =
      this.texts.retrieve;

    let message = defaultMessage;

    switch (requisitionStatus) {
      case RequisitionStatus.Draft:
      case RequisitionStatus.Rejected:
        message = `${defaultMessage}<br><br><b>${draftAndRejectedMessage}</b>`;
        break;
      case RequisitionStatus.Approved:
        message = `${defaultMessage}<br><br><b>${approvedMessage}</b>`;
        break;
      default:
        break;
    }

    return this.dialogService.information({
      width: DialogWidth.SmallXX,
      message,
      title,
      agreeLabel,
    });
  }

  public confirmBulkRetrieve$(requisitions: Requisition[]) {
    const { defaultMessage, title, agreeLabel } = this.texts.bulkRetrieve(requisitions.length);

    const { approvedCount, draftCount } = requisitions.reduce(
      (acc, req) => {
        const approvedCount =
          req.status === RequisitionStatus.Approved ? acc.approvedCount + 1 : acc.approvedCount;
        const draftCount =
          req.status === RequisitionStatus.Draft ? acc.draftCount + 1 : acc.draftCount;
        return { ...acc, approvedCount, draftCount };
      },
      { approvedCount: 0, draftCount: 0 },
    );

    let message = defaultMessage;

    if (approvedCount && draftCount) {
      message = `${defaultMessage}<br><br><ul><li>${
        this.texts.bulkRetrieve(approvedCount).approvedMessage
      }</li><li>${this.texts.bulkRetrieve(draftCount).draftMessage}</li></ul>`;
    } else if (approvedCount) {
      message = `${defaultMessage}<br><br><ul><li>${
        this.texts.bulkRetrieve(approvedCount).approvedMessage
      }</li></ul>`;
    } else if (draftCount) {
      message = `${defaultMessage}<br><br><ul><li>${
        this.texts.bulkRetrieve(draftCount).draftMessage
      }</li></ul>`;
    }

    return this.dialogService.information({
      width: DialogWidth.SmallXX,
      message,
      title,
      agreeLabel,
    });
  }

  public confirmDeleteOpening$(count: number): Observable<boolean> {
    return this.dialogService.error({ ...this.texts.deleteOpenings(count), safeFooter: false });
  }

  public confirmDeleteInvitation$(): Observable<boolean> {
    return this.dialogService.error({ ...this.texts.deleteInvitation });
  }

  public confirmRemoveTeamMember$(): Observable<boolean> {
    return this.dialogService.error({ ...this.texts.removeTeamMember, safeFooter: false });
  }

  public confirmDeleteAttachment$(): Observable<boolean> {
    return this.dialogService.error({ ...this.texts.deleteAttachment });
  }

  public confirmDeleteRequisition$(name: string): Observable<boolean> {
    const { checkboxLabel, ...restTexts } = this.texts.deleteRequisition(name);
    return this.dialogService.error({
      ...restTexts,
      checkboxes: [{ title: checkboxLabel }],
    });
  }

  public confirmChangeDepartment$(context: UpdateContext): Observable<boolean> {
    return this.dialogService.warning({
      ...(context === UpdateContext.ApprovalRequest
        ? this.texts.changeDepartmentRequestApproval
        : this.texts.changeDepartment),
    });
  }

  public openApproversSelectDialog$(
    approverIds: number[],
    lockedApproverIds: number[],
    vcRef: ViewContainerRef,
  ): Observable<User[]> {
    const data = {
      heading: this.i18n.translate('app.requisitions.select_approvers_dialog.heading'),
      subheading: this.i18n.translate('app.requisitions.select_approvers_dialog.subheading'),
      assignments: approverIds,
      lockedAssignments: lockedApproverIds,
      lockedAssignmentsTooltip: lockedApproverIds.length
        ? this.i18n.translate('app.requisitions.select_approvers_dialog.cant_remove_tooltip')
        : undefined,
      minSelectedAdmins: 1,
    };

    return this.dialogService
      .openCustom(AdminsSelectDialogComponent, { width: DialogWidth.Small }, data, vcRef)
      .afterClosed.pipe(
        filterDefined,
        map(({ users }: AdminsSelectDialogResult) => users),
      );
  }

  public openApprovalRequest$(data: ApprovalRequestDialogData) {
    return this.dialogService.openCustom(
      ApprovalRequestDialogComponent,
      {
        width: DialogWidth.Large,
      },
      data,
    );
  }

  public openRequisitionApprovedSuccessDialog(
    dialogData: RequisitionApprovedDialogData,
    setViewedPayload: {
      requisitionId: number;
    },
  ): Observable<DialogResult> {
    return this.dialogService
      .openCustom(
        RequisitionApprovedSuccessDialogComponent,
        {
          width: DialogWidth.Small,
        },
        dialogData,
      )
      .afterClosed.pipe(
        tap(() =>
          this.store.dispatch(
            RequisitionsActions.setApprovedDialogViewed({ payload: setViewedPayload }),
          ),
        ),
      );
  }

  public openAssignedJobsDialog$(offers: OfferSimpleListItem[]): Observable<boolean> {
    return this.dialogService.openCustom(
      AssignedJobsDialogComponent,
      {
        width: DialogWidth.Small,
      },
      {
        offers,
      },
    ).afterClosed;
  }
}
